
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Search, UserPlus, Users, MessageSquare } from 'lucide-react';

interface FasClube {
  id: string;
  nome: string;
  cidade: string;
  descricao: string;
  logo: string;
  membros: Member[];
  createdAt: string;
  owner: string;
}

interface Member {
  id: string;
  nome: string;
  foto: string;
  dataEntrada: string;
}

interface Post {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  content: string;
  timestamp: string;
  clubeId: string;
}

interface UserData {
  fullName: string;
  email: string;
  profilePhoto: string;
  isRegistered: boolean;
  isSubscribed: boolean;
}

const FasClubeDetalhe = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [fasClube, setFasClube] = useState<FasClube | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [posts, setPosts] = useState<Post[]>([]);
  const [newPost, setNewPost] = useState('');
  const [user, setUser] = useState<UserData | null>(null);
  const [isMembro, setIsMembro] = useState(false);
  
  useEffect(() => {
    // Load user data
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    
    // Load fan club data
    const storedFasClubes = localStorage.getItem('fasClubes');
    if (storedFasClubes) {
      const clubes = JSON.parse(storedFasClubes);
      const clube = clubes.find((c: FasClube) => c.id === id);
      
      if (clube) {
        setFasClube(clube);
        
        // Check if the current user is a member
        if (userData) {
          const currentUser = JSON.parse(userData);
          const isMember = clube.membros.some((m: Member) => m.nome === currentUser.fullName);
          setIsMembro(isMember);
        }
        
        // Load posts for this club
        const allPosts = JSON.parse(localStorage.getItem('fasClubesPosts') || '[]');
        const clubePosts = allPosts.filter((post: Post) => post.clubeId === id);
        setPosts(clubePosts);
      } else {
        // Handle fan club not found
        toast.error('Fã clube não encontrado');
        navigate('/fas-clubes');
      }
    }
  }, [id, navigate]);
  
  const handleJoinClube = () => {
    if (!user) {
      toast.error("Você precisa estar logado para participar de um fã clube");
      navigate('/login');
      return;
    }
    
    // Get updated fan clubs list
    const storedFasClubes = localStorage.getItem('fasClubes');
    if (storedFasClubes && fasClube) {
      const clubes = JSON.parse(storedFasClubes);
      const clubeIndex = clubes.findIndex((c: FasClube) => c.id === id);
      
      if (clubeIndex !== -1) {
        // Add user as member
        const newMember = {
          id: Date.now().toString(),
          nome: user.fullName,
          foto: user.profilePhoto,
          dataEntrada: new Date().toISOString()
        };
        
        clubes[clubeIndex].membros.push(newMember);
        
        // Update localStorage
        localStorage.setItem('fasClubes', JSON.stringify(clubes));
        
        // Update state
        setFasClube(clubes[clubeIndex]);
        setIsMembro(true);
        toast.success('Você agora é membro deste fã clube!');
      }
    }
  };
  
  const handleLeaveClube = () => {
    if (!user || !fasClube) return;
    
    // Get updated fan clubs list
    const storedFasClubes = localStorage.getItem('fasClubes');
    if (storedFasClubes) {
      const clubes = JSON.parse(storedFasClubes);
      const clubeIndex = clubes.findIndex((c: FasClube) => c.id === id);
      
      if (clubeIndex !== -1) {
        // Remove user from members
        const updatedMembros = clubes[clubeIndex].membros.filter(
          (m: Member) => m.nome !== user.fullName
        );
        
        clubes[clubeIndex].membros = updatedMembros;
        
        // Update localStorage
        localStorage.setItem('fasClubes', JSON.stringify(clubes));
        
        // Update state
        setFasClube(clubes[clubeIndex]);
        setIsMembro(false);
        toast.success('Você saiu deste fã clube');
      }
    }
  };
  
  const handlePostSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Você precisa estar logado para postar");
      return;
    }
    
    if (!newPost.trim()) {
      toast.error("A mensagem não pode estar vazia");
      return;
    }
    
    // Create new post
    const post: Post = {
      id: Date.now().toString(),
      userId: user.email,
      userName: user.fullName,
      userPhoto: user.profilePhoto || '/placeholder.svg',
      content: newPost,
      timestamp: new Date().toISOString(),
      clubeId: id || ''
    };
    
    // Update posts in localStorage
    const allPosts = JSON.parse(localStorage.getItem('fasClubesPosts') || '[]');
    localStorage.setItem('fasClubesPosts', JSON.stringify([...allPosts, post]));
    
    // Update state
    setPosts(prevPosts => [post, ...prevPosts]);
    setNewPost('');
    toast.success('Postagem realizada com sucesso!');
  };
  
  const filteredMembros = fasClube?.membros.filter(membro => 
    membro.nome.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  
  if (!fasClube) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 container mx-auto px-4 pt-24 pb-10 flex justify-center items-center">
          <p className="text-xl">Carregando...</p>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 container mx-auto px-4 pt-24 pb-10">
        <div className="bg-black/60 p-6 rounded-lg mb-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <Avatar className="h-24 w-24 md:h-32 md:w-32">
              <AvatarImage src={fasClube.logo} alt={fasClube.nome} />
              <AvatarFallback>{fasClube.nome.slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-bold">{fasClube.nome}</h1>
              <p className="text-gray-300">{fasClube.cidade}</p>
              {fasClube.descricao && (
                <p className="mt-3 text-gray-200">{fasClube.descricao}</p>
              )}
              <div className="mt-4 text-sm text-gray-400">
                <span>{fasClube.membros.length} membros • Criado por {fasClube.owner}</span>
              </div>
            </div>
            <div className="mt-4 md:mt-0">
              {!isMembro ? (
                <button 
                  onClick={handleJoinClube}
                  className="bg-rustico-brown hover:bg-[#6f5f3f] text-white px-6 py-3 rounded-md flex items-center gap-2 transition-colors"
                >
                  <UserPlus size={18} />
                  Participar
                </button>
              ) : (
                <button 
                  onClick={handleLeaveClube}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-md flex items-center gap-2 transition-colors"
                >
                  Sair do fã clube
                </button>
              )}
            </div>
          </div>
        </div>
        
        <Tabs defaultValue="forum" className="w-full">
          <TabsList className="bg-black/40 rounded-lg p-1 mb-8">
            <TabsTrigger value="forum" className="data-[state=active]:bg-rustico-brown">
              <MessageSquare className="h-4 w-4 mr-2" />
              Fórum
            </TabsTrigger>
            <TabsTrigger value="membros" className="data-[state=active]:bg-rustico-brown">
              <Users className="h-4 w-4 mr-2" />
              Membros
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="forum">
            <div className="bg-black/40 rounded-lg p-6">
              {isMembro ? (
                <form onSubmit={handlePostSubmit} className="mb-8">
                  <div className="flex gap-4">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.profilePhoto || "/placeholder.svg"} alt={user?.fullName} />
                      <AvatarFallback>
                        {user?.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <Input 
                        type="text" 
                        placeholder="Compartilhe algo com o fã clube..."
                        className="bg-[#333] text-white border-none mb-3"
                        value={newPost}
                        onChange={(e) => setNewPost(e.target.value)}
                      />
                      <div className="flex justify-end">
                        <button 
                          type="submit"
                          className="bg-rustico-brown hover:bg-[#6f5f3f] text-white px-4 py-2 rounded-md transition-colors"
                        >
                          Postar
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="bg-gray-800 p-4 rounded-lg mb-8 text-center">
                  <p className="text-gray-300 mb-2">Apenas membros podem participar das discussões</p>
                  <button 
                    onClick={handleJoinClube}
                    className="bg-rustico-brown hover:bg-[#6f5f3f] text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors mx-auto"
                  >
                    <UserPlus size={18} />
                    Participar do fã clube
                  </button>
                </div>
              )}
              
              {posts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400">Nenhuma postagem ainda. Seja o primeiro a postar!</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {posts.map(post => (
                    <div key={post.id} className="bg-black/40 p-4 rounded-lg">
                      <div className="flex items-center mb-3">
                        <Avatar className="h-10 w-10 mr-3">
                          <AvatarImage src={post.userPhoto} alt={post.userName} />
                          <AvatarFallback>{post.userName.charAt(0).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="font-medium">{post.userName}</h4>
                          <p className="text-xs text-gray-400">
                            {new Date(post.timestamp).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <p className="text-gray-200">{post.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="membros">
            <div className="bg-black/40 rounded-lg p-6">
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input 
                    type="text" 
                    placeholder="Buscar membros..." 
                    className="pl-10 bg-[#333] text-white border-none"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              {filteredMembros.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400">
                    {searchTerm ? 'Nenhum membro encontrado.' : 'Este fã clube ainda não tem membros.'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredMembros.map(membro => (
                    <div key={membro.id} className="bg-black/40 p-4 rounded-lg flex items-center">
                      <Avatar className="h-12 w-12 mr-4">
                        <AvatarImage src={membro.foto} alt={membro.nome} />
                        <AvatarFallback>{membro.nome.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="font-medium">{membro.nome}</h4>
                        <p className="text-xs text-gray-400">
                          Entrou em {new Date(membro.dataEntrada).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default FasClubeDetalhe;
